import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SocketContext } from '../services/socketContext';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';

const CallContext = createContext();

const configuration = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    {
      urls: [
        "turn:relay1.expressturn.com:3478",
        "turns:relay1.expressturn.com:5349",
      ],
      credential: "hL4ncsQ1AvcxcX4V",
      username: "efXFF7BTGHP37RINUN",
    },
  ],
  iceTransportPolicy: "all",
};

export const CallProvider = ({ children }) => {
  const { socket } = useContext(SocketContext);
  const { id } = useParams();

  const roomId = id?.split("_")[0] || window.location.pathname?.split("/")[2]?.split("_")[0];
  const loginData = JSON.parse(localStorage.getItem("loginData"));

  const peerConnection = useRef(new RTCPeerConnection(configuration));
  const localStreamRef = useRef(null);
  const incomingCallRef = useRef(null);
  const pendingCandidates = useRef([]);

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callStatus, setCallStatus] = useState('idle'); // 'idle', 'ringing', 'active', 'ended'
  const [callReceived, setCallReceived] = useState(false);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleCallOffer = (data) => {
      console.log('📞 Incoming call-offer:', data);
      setIncomingCall(data);
      incomingCallRef.current = data;
      setCallStatus('ringing');
    };

    const handleCallAnswer = async ({ answer }) => {
      if (peerConnection.current?.signalingState === "stable") return;
      console.log("✅ Received answer");
      try {
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
        setCallStatus('active');
        setCallReceived(true)
      } catch (error) {
        console.error("Error setting remote description:", error);
        endCall();
      }
    };

    const handleIceCandidate = async ({ candidate }) => {
      try {
        if (peerConnection.current && candidate) {
          if (peerConnection.current.remoteDescription) {
            console.log("🧊 Adding ICE candidate");
            await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
          } else {
            console.warn("⛔ Tried to add ICE candidate before remote description was set");
            pendingCandidates.current.push(new RTCIceCandidate(candidate));
          }
        }
      } catch (error) {
        console.error("❌ Error adding ICE candidate", error);
      }
    };

    const handleCallEnded = () => {
      console.log("🔚 Call ended");
      setCallStatus('ended')
      setCallReceived(false)
      endCall();
    };

    const handleCallRejected = ({ reason }) => {
      console.log("❌ Call rejected:", reason);
      setCallStatus('busy');
    };

    socket.on('call-offer', handleCallOffer);
    socket.on('call-answer', handleCallAnswer);
    socket.on('ice-candidate', handleIceCandidate);
    socket.on('call-ended', handleCallEnded);
    socket.on('call-rejected', handleCallRejected);

    return () => {
      socket.off('call-offer', handleCallOffer);
      socket.off('call-answer', handleCallAnswer);
      socket.off('ice-candidate', handleIceCandidate);
      // socket.off('call-ended', handleCallEnded);
      socket.off('call-rejected', handleCallRejected);
      endCall(); // Clean up any active call
    };
  }, [socket]);

  const initializePeerConnection = (targetSocketId = null) => {

    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }

    peerConnection.current = new RTCPeerConnection(configuration);

    peerConnection.current.onicecandidate = (event) => {
      if (event.candidate) {
        console.log("📤 Sending ICE candidate...");
        socket.emit("ice-candidate", {
          targetSocketId: targetSocketId || incomingCallRef.current?.from,
          candidate: event.candidate,
        });
      }
    };

    peerConnection.current.ontrack = (event) => {
      if (event.streams?.[0]) {
        console.log("🎧 Received remote track");
        setRemoteStream(event.streams[0]);
      }
    };

    peerConnection.current.oniceconnectionstatechange = () => {
      const state = peerConnection.current.iceConnectionState;
      console.log("ICE Connection State:", state);
      if (["disconnected", "failed", "closed"].includes(state)) {
        endCall(targetSocketId);
      }
    };

    peerConnection.current.onconnectionstatechange = () => {
      console.log("Connection State:", peerConnection.current.connectionState);
    };

  };

  const getLocalStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      setLocalStream(stream);
      return stream;
    } catch (error) {
      alert("Microphone access denied");
      console.error('Error getting local stream:', error);
      // throw error;
    }
  };

  const startCall = async (data, currChatUser) => {
    try {
      const stream = await getLocalStream();
      initializePeerConnection(data?.socketId);
      if (!stream) { return; }
      stream.getTracks().forEach((track) => {
        peerConnection.current.addTrack(track, stream);
      });

      const offer = await peerConnection.current.createOffer();
      await peerConnection.current.setLocalDescription(offer);

      const payload = {
        targetSocketId: data?.socketId,
        offer,
        from: socket.id,
        caller: loginData?.userName,
        uuid: uuidv4(),
        targetFcmToken: null,
        reciever: { ...currChatUser },
        sender: loginData,
      };

      socket.emit("call-offer", payload);
      setCallStatus('ringing');
    } catch (error) {
      console.error("Error starting call:", error);
      endCall();
      throw error;
    }
  };

  const acceptCall = async () => {
    const callData = incomingCall;
    if (!callData) return;

    try {
      const stream = await getLocalStream();
      initializePeerConnection(callData.from);

      stream.getTracks().forEach((track) => {
        peerConnection.current.addTrack(track, stream);
      });

      await peerConnection.current.setRemoteDescription(
        new RTCSessionDescription(callData.offer)
      );

      pendingCandidates.current.forEach((candidate) => {
        peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
      });
      pendingCandidates.current = [];

      const answer = await peerConnection.current.createAnswer();
      await peerConnection.current.setLocalDescription(answer);

      socket.emit("call-answer", {
        targetSocketId: callData.from,
        answer,
      });

      setCallStatus('active');
    } catch (error) {
      console.error("Error accepting call:", error);
      endCall();
      throw error;
    }
  };

  const endCall = (targetSocketId = null) => {
    if (peerConnection.current) {
      peerConnection.current.onicecandidate = null;
      peerConnection.current.ontrack = null;
      peerConnection.current.oniceconnectionstatechange = null;
      peerConnection.current.onconnectionstatechange = null;
      peerConnection.current.onsignalingstatechange = null;
      peerConnection.current.onnegotiationneeded = null;

      peerConnection.current.close();
      peerConnection.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
      setLocalStream(null);
    }

    if (remoteStream) {
      remoteStream.getTracks().forEach((track) => track.stop());
      setRemoteStream(null);
    }

    setIncomingCall(null);
    pendingCandidates.current = [];

    const target = targetSocketId || incomingCallRef.current?.from;
    if (target) {
      socket.emit("call-end", { targetSocketId: target });
    }

    incomingCallRef.current = null;

    setTimeout(() => {
      initializePeerConnection(targetSocketId || incomingCallRef.current?.from);
      incomingCallRef.current = null;
    }, 500);
  };

  return (
    <CallContext.Provider
      value={{
        localStream,
        remoteStream,
        incomingCall,
        callStatus,
        callReceived,
        getLocalStream,
        startCall,
        acceptCall,
        endCall,
        setIncomingCall,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};